import { CountryConfig, Estimate, EstimateBreakdown, MaterialOption, CalculatorState } from './types'
import { fallbackMaterialRates } from './constants'

export const getMaterialOptions = (materialType: 'all' | 'metal' | 'commercial' | 'repair'): MaterialOption[] => {
  if (materialType === 'metal') {
    return [
      { value: 'metal', label: 'Standard Metal' },
      { value: 'standing_seam', label: 'Standing Seam Metal' },
      { value: 'copper', label: 'Copper Roof' },
    ]
  }
  if (materialType === 'commercial') {
    return [
      { value: 'flat', label: 'Flat Roof' },
      { value: 'tpo', label: 'TPO Roofing' },
      { value: 'rubber', label: 'Rubber Roof (EPDM)' },
    ]
  }
  if (materialType === 'repair') {
    return [
      { value: 'asphalt', label: 'Asphalt Shingle Repair' },
      { value: 'metal', label: 'Metal Roof Repair' },
      { value: 'tile', label: 'Tile Repair' },
    ]
  }
  return [
    { value: 'asphalt', label: 'Asphalt Shingles' },
    { value: 'metal', label: 'Metal Roofing' },
    { value: 'tile', label: 'Clay/Concrete Tile' },
    { value: 'slate', label: 'Slate Roofing' },
    { value: 'recycled_shingles', label: 'Recycled Shingles (Eco)' },
    { value: 'solar_tiles', label: 'Solar Roof Tiles (Eco)' },
    { value: 'green_roof', label: 'Green/Living Roof (Eco)' },
    { value: 'recycled_metal', label: 'Recycled Metal (Eco)' },
    { value: 'composite_slate', label: 'Composite Slate (Eco)' },
  ]
}

export const validateZipCode = (value: string, currentCountry: CountryConfig): string | undefined => {
  if (!value) return `${currentCountry.postalCodeLabel} is required`
  if (!currentCountry.postalCodePattern.test(value)) return `Please enter a valid ${currentCountry.postalCodeLabel.toLowerCase()}`
  return undefined
}

export const validateSquareFootage = (value: string): string | undefined => {
  if (!value) return 'Square footage is required'
  const num = parseFloat(value)
  if (isNaN(num)) return 'Please enter a valid number'
  if (num < 100) return 'Minimum roof size is 100 sq ft'
  if (num > 100000) return 'Maximum roof size is 100,000 sq ft'
  return undefined
}

export const calculateEstimate = (
  state: CalculatorState,
  currentCountry: CountryConfig,
  realTimePricing: any
): Estimate => {
  const sqft = parseFloat(state.squareFootage)
  
  // Use real-time pricing if available, otherwise fallback
  const rates = realTimePricing?.materialRates || fallbackMaterialRates[state.material]
  const regionalMultiplier = (realTimePricing?.regionalMultiplier || 1.0) * currentCountry.priceMultiplier
  
  let baseLow = sqft * rates.low
  let baseHigh = sqft * rates.high

  // Calculate additional costs
  let additionalCosts: EstimateBreakdown = {
    tearOff: 0,
    pitchMultiplier: 1,
    storiesMultiplier: 1,
    chimneyFlashing: 0,
    skylightFlashing: 0,
    complexityFee: 0,
    urgencyFee: 0,
  }

  // Tear off cost
  if (state.removeOldRoof === 'yes') {
    const squares = sqft / 100
    const tearOffPerSquare = state.numLayers === 1 ? 125 : state.numLayers === 2 ? 150 : 175
    additionalCosts.tearOff = squares * tearOffPerSquare
  }

  // Roof pitch multiplier
  if (state.roofPitch === 'medium') {
    additionalCosts.pitchMultiplier = 1.15
  } else if (state.roofPitch === 'steep') {
    additionalCosts.pitchMultiplier = 1.35
  }

  // Multi-story labor increase
  if (state.numStories === 2) {
    additionalCosts.storiesMultiplier = 1.1
  } else if (state.numStories >= 3) {
    additionalCosts.storiesMultiplier = 1.2
  }

  // Chimneys and skylights
  additionalCosts.chimneyFlashing = state.hasChimneys * 350
  additionalCosts.skylightFlashing = state.hasSkylights * 250

  // Roof complexity
  if (state.hasValleys === 'moderate') {
    additionalCosts.complexityFee = sqft * 0.5
  } else if (state.hasValleys === 'many') {
    additionalCosts.complexityFee = sqft * 1.2
  }

  // Urgency premium
  if (state.urgency === 'soon') {
    additionalCosts.urgencyFee = (baseLow + baseHigh) / 2 * 0.05
  } else if (state.urgency === 'urgent') {
    additionalCosts.urgencyFee = (baseLow + baseHigh) / 2 * 0.15
  }

  // Apply multipliers
  baseLow = baseLow * additionalCosts.pitchMultiplier * additionalCosts.storiesMultiplier
  baseHigh = baseHigh * additionalCosts.pitchMultiplier * additionalCosts.storiesMultiplier

  // Add flat fees
  const totalLow = baseLow + additionalCosts.tearOff + additionalCosts.chimneyFlashing + 
                   additionalCosts.skylightFlashing + additionalCosts.complexityFee + 
                   additionalCosts.urgencyFee

  const totalHigh = baseHigh + additionalCosts.tearOff + additionalCosts.chimneyFlashing + 
                    additionalCosts.skylightFlashing + additionalCosts.complexityFee + 
                    additionalCosts.urgencyFee

  return {
    low: totalLow,
    high: totalHigh,
    breakdown: additionalCosts
  }
}
